import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import PasswordStrengthBar from '../components/PasswordStrengthBar';
import PasswordRequirements from '../components/PasswordRequirements';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import { resetPassword, validarTokenReset } from '../api/authApi';
import { ApiError } from '../types/auth.types';
import styles from './ResetPasswordPage.module.css';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState<string | undefined>();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    validarTokenReset(token)
      .catch((error) => {
        const data = axios.isAxiosError<ApiError>(error) ? error.response?.data : undefined;
        setTokenError(data?.mensaje ?? 'El enlace de restablecimiento no es válido o ha expirado.');
      })
      .finally(() => setCheckingToken(false));
  }, [token, navigate]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) {
      return;
    }
    setGeneralError(null);

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setConfirmError('Completá los dos campos de contraseña.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError('Las contraseñas no coinciden.');
      return;
    }
    setConfirmError(undefined);

    setLoading(true);
    try {
      const response = await resetPassword({ token, newPassword, confirmPassword });
      setSuccessMessage(response.mensaje);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (error) {
      if (axios.isAxiosError<ApiError>(error)) {
        const data = error.response?.data;
        if (error.response?.status === 401 || data?.codigo === 'INVALID_TOKEN') {
          setTokenError(data?.mensaje ?? 'El enlace de restablecimiento no es válido o ha expirado.');
        } else {
          setGeneralError(data?.mensaje ?? 'No se pudo restablecer la contraseña.');
        }
      } else {
        setGeneralError('No se pudo restablecer la contraseña.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return null;
  }

  if (checkingToken) {
    return (
      <AuthLayout title="Restablecer contraseña" logoSize={150}>
        <p className={styles.success}>Verificando el enlace...</p>
      </AuthLayout>
    );
  }

  if (tokenError) {
    return (
      <AuthLayout title="Enlace inválido" logoSize={150}>
        <p className={styles.errorText}>{tokenError}</p>
        <Link to="/forgot-password" className={styles.backLink}>
          Solicitar un nuevo enlace
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Restablecer contraseña">
      {successMessage ? (
        <p className={styles.success}>{successMessage}</p>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {generalError && <ErrorBanner message={generalError} onClose={() => setGeneralError(null)} />}

          <PasswordInput
            id="newPassword"
            label="Nueva contraseña"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
          />
          <PasswordStrengthBar password={newPassword} />
          <PasswordRequirements password={newPassword} />

          <PasswordInput
            id="confirmPassword"
            label="Confirmar nueva contraseña"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            error={confirmError}
          />

          <Button type="submit" loading={loading} loadingText="Actualizando...">
            Actualizar contraseña
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
